import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Upload, Trash2, RefreshCw, FileText, Database, File, CheckCircle, XCircle, AlertCircle, Clock, HardDrive, FileText as FileTextIcon, Hash, Download, ExternalLink, Archive } from 'lucide-react';
import './DocumentsPage.css';

const API_BASE_URL = '/api';

function DocumentsPage() {
  const [documents, setDocuments] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [includeChunks, setIncludeChunks] = useState(false);

  const fetchDocuments = async () => {
    setLoading(true);
    setError('');
    try {
      // Ajouter un timestamp pour éviter le cache
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_BASE_URL}/documents`, {
        params: {
          include_chunks: includeChunks,
          t: timestamp
        }
      });
      setDocuments(response.data.documents || []);
    } catch (error) {
      setError(`Erreur lors de la récupération des documents: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      // Ajouter un timestamp pour éviter le cache
      const timestamp = new Date().getTime();
      const response = await axios.get(`${API_BASE_URL}/stats?t=${timestamp}`);
      setStats(response.data);
    } catch (error) {
      console.error('Erreur lors de la récupération des statistiques:', error);
    }
  };

  useEffect(() => {
    fetchDocuments();
    fetchStats();
  }, [includeChunks]);

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.pdf')) {
      setError('Seuls les fichiers PDF sont supportés');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('file', file);

    try {
      const response = await axios.post(`${API_BASE_URL}/ingest`, formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      });

      setSuccess(`Document "${file.name}" ingéré avec succès`);
      await fetchDocuments();
      await fetchStats();
    } catch (error) {
      setError(`Erreur lors de l'ingestion: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleZipUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith('.zip')) {
      setError('Seuls les fichiers ZIP sont supportés');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    const formData = new FormData();
    formData.append('archive_file', file);

    // Debug info
    console.log('Uploading ZIP file:', file.name);
    console.log('File size:', file.size, 'bytes');
    console.log('FormData entries:', Array.from(formData.entries()));

    try {
      // Configuration axios optimisée pour les gros fichiers
      const response = await axios.post(`${API_BASE_URL}/ingest_directory`, formData, {
        headers: {
          // Laisser axios définir automatiquement le Content-Type avec boundary
        },
        maxContentLength: Infinity,
        maxBodyLength: Infinity,
        timeout: 300000, // 5 minutes timeout pour les gros fichiers
        onUploadProgress: (progressEvent) => {
          const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          console.log(`Upload progress: ${percentCompleted}%`);
        }
      });

      setSuccess(`Lot de ${response.data.pdf_count || 0} PDFs traité avec succès (${response.data.successful_files || 0} réussis, ${response.data.failed_files || 0} échoués)`);

      // Attendre un peu que le backend ait fini de traiter
      await new Promise(resolve => setTimeout(resolve, 1000));

      await fetchDocuments();
      await fetchStats();
    } catch (error) {
      console.error('Upload error details:', error.response);
      console.error('Upload error config:', error.config);
      setError(`Erreur lors de l'ingestion du lot: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
      event.target.value = '';
    }
  };

  const handleDeleteDocument = async (documentId) => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer ce document ?')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      await axios.delete(`${API_BASE_URL}/documents/${documentId}`);
      setSuccess('Document supprimé avec succès');
      await fetchDocuments();
      await fetchStats();
    } catch (error) {
      setError(`Erreur lors de la suppression: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAllDocuments = async () => {
    if (!window.confirm('Êtes-vous sûr de vouloir supprimer TOUS les documents ? Cette action est irréversible.')) return;

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/documents`);
      setSuccess(response.data.message);
      await fetchDocuments();
      await fetchStats();
    } catch (error) {
      setError(`Erreur lors de la suppression: ${error.response?.data?.detail || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return 'N/A';
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    if (bytes === 0) return '0 Bytes';
    const i = parseInt(Math.floor(Math.log(bytes) / Math.log(1024)));
    return Math.round(bytes / Math.pow(1024, i) * 100) / 100 + ' ' + sizes[i];
  };

  const downloadDocument = async (fileName) => {
    try {
      const downloadUrl = `${API_BASE_URL}/documents/${encodeURIComponent(fileName)}/download`;

      const response = await fetch(downloadUrl, {
        method: 'HEAD'
      });

      if (response.ok) {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.target = '_blank';
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      } else {
        alert(`Le document "${fileName}" n'est pas disponible au téléchargement.`);
      }
    } catch (error) {
      console.error("Erreur lors du téléchargement:", error);
      alert(`Erreur lors du téléchargement: ${error.message}`);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString('fr-FR');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 p-2 sm:p-4">
      <div className="max-w-7xl mx-auto px-2 sm:px-4">
        {/* Header Card */}
        <div className="medical-card mb-4 sm:mb-6 animate-fade-in">
          <div className="medical-card-header px-3 sm:px-6 py-3 sm:py-4">
            <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <div className="bg-gradient-to-r from-green-500 to-blue-600 rounded-full p-2 sm:p-3">
                  <FileText className="h-6 w-6 sm:h-8 sm:w-8 text-white" />
                </div>
                <div>
                  <h2 className="text-xl sm:text-2xl font-bold text-gray-900">PaperFlow</h2>
                  <p className="text-sm sm:text-base text-gray-600">Upload et gestion des articles scientifiques</p>
                </div>
              </div>
              <div className="flex flex-wrap gap-1 sm:gap-2 justify-center lg:justify-start">
                <input
                  type="file"
                  id="file-upload"
                  className="hidden"
                  accept=".pdf"
                  onChange={handleFileUpload}
                  disabled={loading}
                />
                <label
                  htmlFor="file-upload"
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 disabled:bg-gray-400 cursor-pointer transition-colors text-xs sm:text-sm"
                >
                  <Upload className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Ajouter PDF</span>
                </label>
                <input
                  type="file"
                  id="zip-upload"
                  className="hidden"
                  accept=".zip"
                  onChange={handleZipUpload}
                  disabled={loading}
                />
                <label
                  htmlFor="zip-upload"
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 disabled:bg-gray-400 cursor-pointer transition-colors text-xs sm:text-sm"
                >
                  <Archive className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Ajouter ZIP</span>
                </label>
                <button
                  onClick={fetchDocuments}
                  disabled={loading}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors text-xs sm:text-sm"
                >
                  <RefreshCw className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Actualiser</span>
                </button>
                <button
                  onClick={handleDeleteAllDocuments}
                  disabled={loading || documents.length === 0}
                  className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 transition-colors text-xs sm:text-sm"
                >
                  <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                  <span>Tout supprimer</span>
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Status Messages */}
        {error && (
          <div className="mb-4 p-4 bg-red-50 border border-red-200 rounded-lg flex items-center space-x-2 animate-fade-in">
            <AlertCircle className="h-5 w-5 text-red-600" />
            <span className="text-red-800">{error}</span>
          </div>
        )}
        {success && (
          <div className="mb-4 p-4 bg-green-50 border border-green-200 rounded-lg flex items-center space-x-2 animate-fade-in">
            <CheckCircle className="h-5 w-5 text-green-600" />
            <span className="text-green-800">{success}</span>
          </div>
        )}

        {/* Stats Card */}
        <div className="medical-card mb-4 sm:mb-6">
          <div className="medical-card-header px-3 sm:px-6 py-3 sm:py-4">
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">Statistiques du Système</h3>
          </div>
          <div className="medical-card-body px-3 sm:px-6 py-3 sm:py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2 sm:gap-4">
              <div className="bg-blue-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-blue-600">{documents.length}</div>
                <div className="text-sm text-blue-800 flex items-center justify-center space-x-1">
                  <File className="h-4 w-4" />
                  <span>Documents</span>
                </div>
                <div className="text-xs text-blue-600 mt-1">
                  {documents.reduce((total, doc) => total + (doc.chunks_count || 0), 0)} chunks
                </div>
              </div>
              <div className="bg-green-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-green-600">
                  {stats.status?.qdrant_connected ? (
                    <CheckCircle className="h-6 w-6 mx-auto" />
                  ) : (
                    <XCircle className="h-6 w-6 mx-auto" />
                  )}
                </div>
                <div className="text-sm text-green-800 flex items-center justify-center space-x-1">
                  <Database className="h-4 w-4" />
                  <span>Qdrant</span>
                </div>
              </div>
              <div className="bg-purple-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-purple-600">
                  {stats.system?.llm_info ? (
                    <CheckCircle className="h-6 w-6 mx-auto" />
                  ) : (
                    <XCircle className="h-6 w-6 mx-auto" />
                  )}
                </div>
                <div className="text-sm text-purple-800 flex items-center justify-center space-x-1">
                  <FileTextIcon className="h-4 w-4" />
                  <span>LLM</span>
                </div>
              </div>
              <div className="bg-orange-50 rounded-lg p-4 text-center">
                <div className="text-2xl font-bold text-orange-600">
                  {stats.system?.model_info ? (
                    <CheckCircle className="h-6 w-6 mx-auto" />
                  ) : (
                    <XCircle className="h-6 w-6 mx-auto" />
                  )}
                </div>
                <div className="text-sm text-orange-800 flex items-center justify-center space-x-1">
                  <Hash className="h-4 w-4" />
                  <span>Embeddings</span>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Options */}
        <div className="medical-card mb-6">
          <div className="medical-card-body">
            <label className="flex items-center space-x-3 cursor-pointer">
              <input
                type="checkbox"
                checked={includeChunks}
                onChange={(e) => setIncludeChunks(e.target.checked)}
                className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
              />
              <span className="text-gray-700">Inclure les détails des chunks</span>
            </label>
          </div>
        </div>

        {/* Content */}
        {loading ? (
          <div className="medical-card">
            <div className="flex items-center justify-center p-4 sm:p-8">
              <div className="flex items-center space-x-2 sm:space-x-3">
                <RefreshCw className="h-5 w-5 sm:h-6 sm:w-6 animate-spin text-blue-600" />
                <span className="text-sm sm:text-base text-gray-600">Chargement...</span>
              </div>
            </div>
          </div>
        ) : documents.length === 0 ? (
          <div className="medical-card">
            <div className="flex items-center justify-center p-6 sm:p-12 text-center">
              <div>
                <Database className="h-12 w-12 sm:h-16 sm:w-16 mx-auto mb-3 sm:mb-4 text-gray-400" />
                <h3 className="text-lg sm:text-xl font-semibold text-gray-700 mb-2">Aucun document</h3>
                <p className="text-sm sm:text-base text-gray-600 mb-3">Aucun document n'a été ingéré dans la base de données.</p>
                <p className="text-sm sm:text-base text-gray-600">Utilisez le bouton "Ajouter PDF" pour commencer.</p>
              </div>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 sm:gap-6">
            {documents.map((doc, index) => (
              <div key={index} className="medical-card animate-slide-up">
                <div className="medical-card-header px-3 sm:px-6 py-3 sm:py-4">
                  <div className="flex items-center justify-between">
                    <h3 className="text-sm sm:text-base font-semibold text-gray-900 leading-tight" style={{ wordBreak: 'break-word' }}>
                      {doc.pdf_title || doc.file_name}
                    </h3>
                    <File className="h-4 w-4 sm:h-5 sm:w-5 text-red-500 flex-shrink-0 ml-2" />
                  </div>
                </div>
                <div className="medical-card-body px-3 sm:px-6 py-3 sm:py-4">
                  <div className="space-y-2 sm:space-y-3">
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Fichier:</span>
                      <span className="text-xs font-medium text-gray-900 leading-tight" style={{ wordBreak: 'break-word' }}>
                        {doc.file_name}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center space-x-1">
                        <HardDrive className="h-4 w-4" />
                        <span>Taille:</span>
                      </span>
                      <span className="text-sm font-medium text-gray-900">{formatFileSize(doc.file_size)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center space-x-1">
                        <FileTextIcon className="h-4 w-4" />
                        <span>Pages:</span>
                      </span>
                      <span className="text-sm font-medium text-gray-900">{doc.total_pages}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600 flex items-center space-x-1">
                        <Hash className="h-4 w-4" />
                        <span>Chunks:</span>
                      </span>
                      <span className="text-sm font-medium text-gray-900">{doc.chunks_count}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="text-sm text-gray-600">Texte:</span>
                      <span className="text-sm font-medium text-gray-900">{doc.total_text_length?.toLocaleString()} caractères</span>
                    </div>
                    {includeChunks && (
                      <>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Auteur:</span>
                          <span className="text-sm font-medium text-gray-900">{doc.pdf_author || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600">Type:</span>
                          <span className="text-sm font-medium text-gray-900">{doc.document_type || 'N/A'}</span>
                        </div>
                        <div className="flex items-center justify-between">
                          <span className="text-sm text-gray-600 flex items-center space-x-1">
                            <Clock className="h-4 w-4" />
                            <span>Création:</span>
                          </span>
                          <span className="text-sm font-medium text-gray-900">{formatDate(doc.created_at)}</span>
                        </div>
                      </>
                    )}
                  </div>
                </div>
                <div className="medical-card-footer space-y-2">
                  <button
                    onClick={() => downloadDocument(doc.file_name)}
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-blue-500 text-white rounded-lg hover:bg-blue-600 disabled:bg-gray-400 transition-colors text-xs sm:text-sm"
                  >
                    <Download className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Ouvrir PDF</span>
                  </button>
                  <button
                    onClick={() => handleDeleteDocument(doc.file_name)}
                    disabled={loading}
                    className="w-full flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-4 py-2 bg-red-500 text-white rounded-lg hover:bg-red-600 disabled:bg-gray-400 transition-colors text-xs sm:text-sm"
                  >
                    <Trash2 className="h-3 w-3 sm:h-4 sm:w-4" />
                    <span>Supprimer</span>
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default DocumentsPage;